import { useQuery } from '@tanstack/react-query';
import { useQuery as useUrqlQuery } from 'urql';

import { useAuthStore } from '@revelio/auth';
import {
  type CustomRoleTaxonomySelection,
  ROLE_K17000_SELECTION_LIST,
} from '@revelio/data-access';

import { environment } from '../../../../environments/environment';
import { TanstackQueryError } from '../../../../shared/data-fetch/tanstack-query-provider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../filters/hooks/usePrimaryFilterState';
import {
  FilterId,
  type PrimaryFilterId,
  type SingleValueFilter,
} from '../../filters/types/filters';
import { generateSentimentPayload } from '../../filters/utils/generateSentimentReviewPayload';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
  DataType,
  type TextDataSet,
} from './types';

type SentimentReviewMetric = 'positive_review' | 'negative_review';

type SentimentReviewDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  metric: SentimentReviewMetric;
  children: DataProviderChildren<TextDataSet | undefined>;
};

type ReviewType = 'pos' | 'neg';
type ReviewData = { success: true; review: string; reviewType: ReviewType };
type ReviewError = { success: false; error: string };

const getSelectionIdNumber = (selectionId: string): number | null => {
  const lastToken = selectionId.split('_').slice(-1)[0];
  const parsed = Number(lastToken);
  return Number.isFinite(parsed) ? parsed : null;
};

export const SentimentReviewDataProvider = ({
  primaryDimension,
  metric,
  children,
}: SentimentReviewDataProviderProps) => {
  const { dashMetaCsrfToken: csrfToken = 'missing' } = useAuthStore();
  const effectiveFilters = useEffectiveFilters();
  const { config } = useTabContentConfig();
  const { primaryFilter } = usePrimaryFilterState(primaryDimension);

  const isSnapshotDate = (() => {
    return (
      config.id === 'company-sentiment-snapshot' ||
      config.id === 'geography-sentiment-snapshot' ||
      config.id === 'role-sentiment-snapshot'
    );
  })();

  const roleTaxonomyFilterState = effectiveFilters.find(
    (filter) => filter.id === FilterId.ROLE_TAXONOMY
  ) as SingleValueFilter<CustomRoleTaxonomySelection>;

  const [{ data: roleK17000SelectionList }] = useUrqlQuery({
    query: ROLE_K17000_SELECTION_LIST,
    variables: { taxonomyId: roleTaxonomyFilterState?.value },
  });

  const reviewType: ReviewType = metric === 'negative_review' ? 'neg' : 'pos';

  const selectedPrimarySelection = (() => {
    const primarySelections = primaryFilter?.primarySelections ?? [];
    const active =
      primarySelections.find((s) => s.isActive) ?? primarySelections[0];
    return active ?? null;
  })();

  const payload = (() => {
    if (!selectedPrimarySelection) return null;
    const id = getSelectionIdNumber(selectedPrimarySelection.id);
    if (id === null) return null;

    return generateSentimentPayload({
      filters: effectiveFilters,
      roleK17000SelectionList,
      primaryFilter: primaryDimension,
      reviewType,
      selectedEntity: {
        id,
        label: selectedPrimarySelection.name,
        selectionListId: selectedPrimarySelection.levelId,
      },
      isSnapshotDate,
    });
  })();

  const query = useQuery({
    queryKey: ['sentiment-review', payload],
    queryFn: async (): Promise<ReviewData | ReviewError | null> => {
      if (!payload) return null;
      const requestID = crypto.randomUUID();

      const response = await fetch(
        `${environment.DASH_META_API}/api/sentiment/reviews`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'x-surf-token-dash-meta': csrfToken,
            'request-id': requestID,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new TanstackQueryError(
          'Failed to fetch sentiment reviews',
          requestID
        );
      }

      return response.json() as Promise<ReviewData | ReviewError>;
    },
    enabled: !!payload,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const status: DataProviderQueryStatus = (() => {
    if (!payload) return DataProviderQueryStatus.Idle;
    if (query.isFetching) return DataProviderQueryStatus.Fetching;
    if (query.isError) return DataProviderQueryStatus.Error;
    return DataProviderQueryStatus.Success;
  })();

  const data: TextDataSet | undefined = (() => {
    if (!selectedPrimarySelection) return undefined;
    if (!query.data) return undefined;
    const text =
      query.data.success === true ? query.data.review : query.data.error || '';

    return {
      type: DataType.TEXT,
      data: [
        {
          entity: selectedPrimarySelection.name,
          text,
          metadata:
            query.data.success === true
              ? { reviewType: query.data.reviewType }
              : undefined,
        },
      ],
    };
  })();

  const error = query.error instanceof Error ? query.error : undefined;

  return children({ data, status, error });
};
